import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Tag, Sparkles } from 'lucide-react';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';

interface MemberTagDialogProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
  currentTag?: string | null;
  onSaved: () => void;
}

const TAG_SUGGESTIONS = [
  'Secretary',
  'Treasurer',
  'President',
  'Vice President',
  'Director',
  'Founder',
  'Advisor',
  'Committee Head',
  'Coordinator',
  'Volunteer',
];

export function MemberTagDialog({
  isOpen,
  onClose,
  communityId,
  currentTag,
  onSaved,
}: MemberTagDialogProps) {
  const [tag, setTag] = useState(currentTag || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axiosInstance.patch(`/api/communities/${communityId}/membership`, {
        tag: tag.trim(),
      });
      toast.success(tag.trim() ? 'Tag updated!' : 'Tag removed!');
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to update tag');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    setTag('');
    setIsSaving(true);
    try {
      await axiosInstance.patch(`/api/communities/${communityId}/membership`, {
        tag: '',
      });
      toast.success('Tag removed!');
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to remove tag');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Tag className="h-5 w-5 text-primary" />
            Set Your Member Tag
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <p className="text-sm text-muted-foreground">
            Add a tag to describe your role or title in this community. This will be visible to
            other members on your card.
          </p>

          <Input
            placeholder="e.g. Secretary, Treasurer, Founder..."
            value={tag}
            onChange={(e) => setTag(e.target.value.slice(0, 50))}
            maxLength={50}
            className="h-11"
            autoFocus
          />

          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{tag.length}/50</span>
            {currentTag && (
              <button
                onClick={handleRemove}
                disabled={isSaving}
                className="text-xs text-destructive hover:underline"
              >
                Remove tag
              </button>
            )}
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-xs font-medium text-muted-foreground">Suggestions</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {TAG_SUGGESTIONS.map((suggestion) => (
                <Badge
                  key={suggestion}
                  variant="outline"
                  className="cursor-pointer hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-colors text-xs px-2.5 py-1"
                  onClick={() => setTag(suggestion)}
                >
                  {suggestion}
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={isSaving} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || (!tag.trim() && !currentTag)}
              className="flex-1"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {tag.trim() ? 'Save Tag' : 'Remove Tag'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
